import { useCallback, useState } from "react";
import { motion } from "framer-motion";
import "../../styles/Authentication.scss";
import { useConfigContext } from "../../context/ConfigContext";
import { useLabelsContext } from "../../context/LabelsContext";
import { useUserContext } from "../../context/UserContext";
import { sendOTP, validateOTP } from "../../services/auth-service";
import { ApiResponse, ApiStatus } from "../../models/api-response";
import { fadeScale } from "../../styles/MotionFramerAnimations";
import { OtpFormHeader } from "./OtpFormHeader";
import { useOtpForm } from "../../hooks/useOtpForm";
import { OTPValidationResponse } from "../../models/otp-validation-response";
import { useClubContext } from "../../context/ClubContext";
import { useAuthContext } from "../../context/AuthContext";
import { LOGIN_METHODS } from "../../models/login-methods";
import { InterestedUser } from "../../models/interested-user";
import { dispatchAnalyticsEvent } from "../../analytics/dataLayer";
import { useFlashy } from "../../hooks/useFlashy";
import { FLASHY_EVENTS } from "../../flashy/FLASHY_EVENTS";

type OtpFormProps = {
	handleAuthFlow: () => void;
};

export const OtpForm: React.FC<OtpFormProps> = ({ handleAuthFlow }) => {
	const { labels } = useLabelsContext();
	const { config } = useConfigContext();
	const [validatingToken, setValidatingToken] = useState<boolean>(false);
	const [sendingToken, setSendingToken] = useState<boolean>(false);
	const { user, setUser } = useUserContext();
	const { club } = useClubContext();
	const { sendEvent: sendFlashyEcommerceEvent } = useFlashy();
	const { otpMethod, loginMethod, interestedUser, setInterestedUser } =
		useAuthContext();
	const {
		register,
		handleSubmit,
		trigger,
		setError,
		errors,
		otpForm,
		getErrorMessage,
	} = useOtpForm();

	const verifyInterestedUser = (user: InterestedUser): void => {
		if (!interestedUser?.verified) {
			setInterestedUser({
				...user,
				verified: true,
			});
		}
	};

	const scrollToRegistrationForm = (): void => {
		const registrationForm = document.querySelector(
			".registration-form-container",
		);
		registrationForm?.scrollIntoView({
			behavior: "smooth",
			block: "center",
		});
	};
	const dispatchLoginEvent = (userData: OTPValidationResponse): void => {
		dispatchAnalyticsEvent({
			event: "submit_customer_details",
			data: {
				signup_branch: window.location.hostname,
				signup_source: "website",
				loyalty_membership: true,
				action: "login",
				language: config?.language,
				agree_newsletters: userData.agreeToMailing,
			},
		});
	};
	const onSubmit = (otpForm: { otp: string }): void => {
		setValidatingToken(true);

		validateOTP(
			config,
			otpForm.otp,
			otpMethod,
			club?.bookingDomain,
			interestedUser?.GuestId ?? null,
		)
			.then(
				(
					response: ApiResponse<
						OTPValidationResponse | InterestedUser
					>,
				) => {
					// Handle error response
					if (
						response.Status === ApiStatus.Error &&
						response?.Errors?.length
					) {
						setError("otp", {
							type: "manual",
							message: response.Errors[0].Message,
						});
						setValidatingToken(false);
						return;
					}
					// Handle successful response
					if (
						response.Status === ApiStatus.Success &&
						response.Data
					) {
						const data = response.Data as OTPValidationResponse;

						if (interestedUser) {
							verifyInterestedUser(data as InterestedUser);
							setTimeout(() => {
								scrollToRegistrationForm();
							}, 50);
							return;
						}

						setUser({
							LoggedIn: true,
							Name: data?.FirstName,
							LastName: data?.LastName,
							Email: data?.Email,
						});
						// analytics - login event
						dispatchLoginEvent(data);

						// flashy - set customer
						sendFlashyEcommerceEvent(FLASHY_EVENTS.SET_CUSTOMER, {
							email: data?.Email,
						});
						handleAuthFlow();
					}
				},
			)
			.finally(() => {
				setValidatingToken(false);
			});
	};

	const onSendAgain = useCallback(() => {
		setSendingToken(true);

		const getLoginData = () => {
			switch (loginMethod) {
				case LOGIN_METHODS.PASSPORT:
					return user?.Passport ?? "";
				case LOGIN_METHODS.EMAIL:
					return user?.Email ?? "";
				case LOGIN_METHODS.GUESTID:
					return user?.GuestId ?? "";
			}
		};

		sendOTP(
			config,
			otpMethod,
			loginMethod,
			getLoginData(),
			club?.bookingDomain,
		)
			.then((response: ApiResponse<string>) => {
				if (
					response.Status === ApiStatus.Error &&
					response?.Errors?.length
				) {
					setError("otp", {
						type: "manual",
						message: response.Errors[0].Message,
					});
				}
			})
			.catch(() => {
				setError("otp", {
					type: "manual",
					message: labels?.["ApiErrorsLabels1"],
				});
			})
			.finally(() => setSendingToken(false));
	}, []);

	return (
		<form className='simplex-club-form' onSubmit={handleSubmit(onSubmit)}>
			<div className='form-col'>
				<OtpFormHeader />
				{getErrorMessage("otp") && (
					<motion.div {...fadeScale} className='error-container'>
						<span
							className={`error-message key-${getErrorMessage("otp")?.key}`}
						>
							{getErrorMessage("otp")?.message}
						</span>
					</motion.div>
				)}
				<div className={`form-field ${errors.otp ? "has-error" : ""}`}>
					<input
						id='otp'
						type='text'
						placeholder=' '
						{...register("otp", otpForm.otp)}
						onBlur={() => trigger("otp")}
						autoFocus
					/>
					<label className='form-label' htmlFor='otp'>
						{labels?.["InsertToken"]}
					</label>
				</div>
				<button
					type='submit'
					className='form-submit'
					disabled={validatingToken}
				>
					{validatingToken && (
						<span className='loader secondary'></span>
					)}
					{labels?.["LoginFormSubmitText"]}
				</button>
				<div className='form-secondary-action'>
					{sendingToken && <span className='loader primary'></span>}
					<button
						type='button'
						onClick={onSendAgain}
						disabled={sendingToken}
					>
						{labels?.["SendAgainMailOrSms"]}
					</button>
				</div>
			</div>
		</form>
	);
};
